import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {
  AlarmClock,
  AlarmClockCheck,
  CircleCheck,
  RotateCcw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  UserCheck,
} from "lucide-mui";
import FormattedDate from "../FormattedDate.tsx";
import { statusStyle } from "../../utils/statusColors.ts";
import { isBackFromSnooze, isSnoozed } from "./snooze.ts";
import type { ReviewSubject } from "./types.ts";

/** A `prospect_status` chip's colour and label, from the shared registry. */
function styleOf(status: string) {
  const { color, label } = statusStyle("prospect_status", status);
  return { color, label, size: "small" as const };
}

/**
 * Where a reviewed row stands: waiting to continue, to review, pursuing, done,
 * disqualified, snoozed or back from a snooze; for a prospect also reopened by a
 * new hit, and how many hits came since the last decision.
 */
export default function ReviewStatusChips({
  subject: s,
  reopenedAt,
  newHits = 0,
}: {
  subject: ReviewSubject;
  reopenedAt?: string | null;
  newHits?: number;
}) {
  const chips = [];
  if (s.awaiting_approval) {
    chips.push(
      <Tooltip key="wait" title="A workflow step is waiting for someone to continue.">
        <Chip {...styleOf("awaiting_approval")} icon={<UserCheck />} />
      </Tooltip>,
    );
  }
  if (s.done_at) {
    chips.push(
      <Tooltip
        key="done"
        title={
          <>
            Marked done <FormattedDate value={s.done_at} />
          </>
        }
      >
        <Chip {...styleOf("done")} variant="outlined" icon={<CircleCheck />} />
      </Tooltip>,
    );
  } else if (s.review_state === "accepted") {
    chips.push(<Chip key="pursuing" {...styleOf("accepted")} icon={<ThumbsUp />} />);
  } else if (s.review_state === "disqualified") {
    chips.push(
      <Chip key="dropped" {...styleOf("disqualified")} variant="outlined" icon={<ThumbsDown />} />,
    );
  } else if (s.review_state === "pending" && !s.awaiting_approval) {
    chips.push(<Chip key="review" {...styleOf("pending")} variant="outlined" />);
  }
  if (reopenedAt) {
    chips.push(
      <Tooltip
        key="reopened"
        title={
          <>
            Finished, then prospected again <FormattedDate value={reopenedAt} />; deciding on it
            clears this
          </>
        }
      >
        <Chip {...styleOf("reopened")} icon={<RotateCcw />} />
      </Tooltip>,
    );
  }
  if (newHits > 0 && s.review_state !== "pending") {
    chips.push(
      <Tooltip key="new" title="Prospected again since the last decision on it">
        <Chip
          {...styleOf("new_hits")}
          variant="outlined"
          icon={<Sparkles />}
          label={`${newHits} new`}
        />
      </Tooltip>,
    );
  }
  if (isBackFromSnooze(s.snoozed_until)) {
    chips.push(
      <Tooltip
        key="back"
        title={
          <>
            Snoozed until <FormattedDate value={s.snoozed_until!} />; deciding on it clears this
          </>
        }
      >
        <Chip {...styleOf("back_from_snooze")} icon={<AlarmClockCheck />} />
      </Tooltip>,
    );
  } else if (isSnoozed(s.snoozed_until)) {
    chips.push(
      <Chip
        key="snooze"
        {...styleOf("snoozed")}
        variant="outlined"
        icon={<AlarmClock />}
        label={
          <>
            Until <FormattedDate value={s.snoozed_until!} />
          </>
        }
      />,
    );
  }
  if (chips.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {chips}
    </Stack>
  );
}
